import { isDocQuotaBlocked, accountQuotaBlocked } from "../../../engine/services/quota-state.js";
/**
 * Sync Orchestrator
 * The classic interface's side of sync: event wiring, network listeners,
 * the server indicator, presence. Opening and closing a map, with its sync,
 * is the engine's (engine/open-map.js).
 * Extracted from main.js for better separation of concerns.
 *
 * Uses callback injection pattern to receive dependencies from main.js,
 * avoiding circular imports.
 */

import { getSyncPauses } from '../../../engine/sync/sync-pause.js';
import { on, emit, Events } from "../../../engine/core/events.js";
import { serverStatus, needsProbe, ServerLevel, hintText } from "../../../engine/core/server-status.js";
import { knownClearRisk } from "../../../engine/services/device-storage.js";
import { storageOf } from "../../../engine/core/vault-folders.js";
import { ConnectionState } from "../../../engine/sync/websocket.js";
import { getDocState, saveDocState } from "../../../shared/local-doc-state.js";
import { performDocumentRevocation } from "../../../engine/sync/share-revocation.js";
import { getActiveSharedDocs, getSharedSyncManager } from "../../../engine/sync/share-lifecycle.js";
import { sendableCount } from "../../../engine/services/waiting-attachments.js";
import { clearAttachmentUrlMap } from "../views/markdown/attachment.js";
import { engine } from "../../../engine/index.js";
import { showRevocationPrompt } from "../ui/modals.js";
import { showToast } from "../ui/toast.js";
import { escapeHtml } from "../../../engine/core/validation.js";
import { t } from "../../../shared/i18n/index.js";

// Module state
let cb = null;

let crdtEventUnsubscribers = [];
// Track shares whose recovery was handled via the deferred (SHARE_REVOCATION_READY) path.
// When performDocumentRevocation emits SHARE_REVOKED for these, the SHARE_REVOKED listener
// must NOT show a second modal or re-open the doc — the deferred handler already did both.
const _deferredRevocationShares = new Set();

/**
 * Initialize sync orchestrator with cb.
 *
 * @param {Object} cbs - Callbacks object with dependencies:
 *   - getCurrentUser: () => User|null
 *   - CRDTBridge: CRDTBridge module
 *   - UserCRDTBridge: UserCRDTBridge module
 *   - AuthHandlers: AuthHandlers module
 *   - Store: Store module
 *   - VaultStorage: VaultStorage module
 *   - VaultUI: VaultUI module
 *   - NotesView: NotesView module
 *   - NotesOverlay: NotesOverlay module
 *   - confirmModal: (options) => Promise<boolean>
 *   - showToast: (message, options) => void
 *   - switchView: (viewName) => void
 *   - markSaved: () => void
 *   - handleDocOpen: (docId) => Promise<void>
 */
export function setupSyncOrchestrator(cbs) {
  cb = cbs;
  // The open map's: who else has it open, and its attachments' links
  // (engine/open-map.js).
  on(Events.PRESENCE_CHANGED, ({ users }) => updatePresenceUI(users));
  on(Events.MAP_CLOSED, () => clearAttachmentUrlMap());
}

/**
 * Clean node for sync comparison (remove transient properties).
 */
export function cleanNodeForSync(node) {
  if (!node) return null;
  const result = {
    id: node.id,
    name: node.name,
    color: node.color,
    children: node.children ? node.children.map(cleanNodeForSync) : [],
  };
  // Copy optional properties that should be synced
  if (node.status) result.status = node.status;
  if (node.tags?.length) result.tags = node.tags;
  if (node.startDate) result.startDate = node.startDate;
  if (node.endDate) result.endDate = node.endDate;
  if (node.notes) result.notes = node.notes;
  if (node.collapsed) result.collapsed = node.collapsed;
  return result;
}

/**
 * Update presence UI (who else is viewing)
 * @param {Array} users - List of users viewing the document
 */
export function updatePresenceUI(users) {
  const presenceEl = document.getElementById("presence-users");
  if (!presenceEl) return;

  if (!users || users.length <= 1) {
    presenceEl.classList.add("hidden");
    return;
  }

  // Filter out current user
  const otherUsers = users.filter((u) => u.id !== cb.getCurrentUser()?.id);
  if (otherUsers.length === 0) {
    presenceEl.classList.add("hidden");
    return;
  }

  presenceEl.classList.remove("hidden");
  presenceEl.innerHTML = '';
  otherUsers.forEach((u) => {
    const dot = document.createElement('span');
    dot.className = 'presence-dot';
    dot.title = u.username || '';
    presenceEl.appendChild(dot);
  });
}

/**
 * Check if the server is reachable by pinging the health endpoint
 * @returns {Promise<boolean>} true if server returns { status: "ok" }, false otherwise
 */
export async function checkServerHealth() {
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);

    const response = await fetch('/health', {
      method: 'GET',
      signal: controller.signal,
    });

    clearTimeout(timeout);

    if (!response.ok) return false;

    const data = await response.json();
    return data.status === 'ok';
  } catch {
    // Any error (network, timeout, parse, wrong structure) = server unavailable
    return false;
  }
}

/**
 * Show modal when server is unreachable
 */
export function showConnectionErrorModal() {
  // Remove existing modal if any
  const existing = document.getElementById("connection-error-modal");
  if (existing) existing.remove();

  const modal = document.createElement("div");
  modal.id = "connection-error-modal";
  modal.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
  `;

  modal.innerHTML = `
    <div style="
      background: var(--bg-primary, #1a1a2e);
      border: 1px solid var(--border-color, #333);
      border-radius: 8px;
      padding: 24px;
      max-width: 400px;
      text-align: center;
    ">
      <h3 style="margin: 0 0 12px 0; color: var(--text-primary, #fff);">${escapeHtml(t("app.connError.title"))}</h3>
      <p style="margin: 0 0 20px 0; color: var(--text-secondary, #aaa);">
        ${escapeHtml(t("app.connError.message"))}
      </p>
      <button id="connection-error-close" style="
        background: var(--accent-cyan, #00d4aa);
        color: var(--text-primary, white);
        border: none;
        padding: 8px 24px;
        border-radius: 6px;
        cursor: pointer;
        font-weight: 500;
      ">${escapeHtml(t("common.ok"))}</button>
    </div>
  `;

  document.body.appendChild(modal);

  document.getElementById("connection-error-close").addEventListener("click", () => {
    modal.remove();
  });

  // Close on backdrop click
  modal.addEventListener("click", (e) => {
    if (e.target === modal) modal.remove();
  });
}

// Last probe of the server (/health) while the connection is in trouble:
// only a probe tells "offline" (unreachable) from "not getting through".
let serverProbe = null; // 'ok' | 'degraded' | 'unreachable' | null
let serverProbeAt = 0;
let serverProbing = false;
let lastServerStatus = null;
// The server ended this device's session (close 4401): until the next sign-in.
let sessionEnded = false;

/** Probe /health: 'ok', 'degraded' (answers but unwell) or 'unreachable'. */
async function probeServer() {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return "unreachable";
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    const response = await fetch("/health", { method: "GET", signal: controller.signal });
    clearTimeout(timeout);
    if (!response.ok) return response.status >= 500 && response.status !== 502 && response.status !== 504 ? "degraded" : "unreachable";
    const data = await response.json().catch(() => null);
    return data?.status === "ok" ? "ok" : "degraded";
  } catch {
    return "unreachable";
  }
}

/** Online maps of the account: their files attached offline are sent. */
function sendableMaps() {
  return (cb?.VaultStorage?.getIndex?.()?.docs || [])
    .filter((doc) => storageOf(doc) === "online" && doc.role !== "viewer").map((doc) => doc.docId);
}

/** The server indicator's last state (for Document Properties and mobile). */
export function getServerStatus() {
  return lastServerStatus || computeServerStatus();
}

function computeServerStatus(mapState = cb?.CRDTBridge?.getConnectionState?.()) {
  const docId = cb?.CRDTBridge?.getCurrentDocId?.();
  const input = {
    signedIn: !!cb?.getCurrentUser?.(),
    quotaBlocked: !!(isDocQuotaBlocked(docId) || accountQuotaBlocked()),
    syncPaused: getSyncPauses().some((pause) => pause.scope === "vault" || pause.id === docId),
    mapStorage: engine.map.storage(),
    mapState: mapState || null,
    vaultState: cb?.UserCRDTBridge?.getConnectionState?.() || null,
    tooManyConnections: !!(cb?.CRDTBridge?.isRefusedTooMany?.() || cb?.UserCRDTBridge?.isRefusedTooMany?.()),
    serverBusy: !!cb?.CRDTBridge?.isServerBusy?.(),
    sessionEnded,
    filesWaiting: sendableCount(sendableMaps()),
    mayBeCleared: knownClearRisk() === true,
  };
  if (typeof navigator !== "undefined" && navigator.onLine === false) serverProbe = "unreachable";
  if (!needsProbe(input)) {
    serverProbe = null;
  } else if (!serverProbing && (serverProbe === null || Date.now() - serverProbeAt > 10000)) {
    serverProbing = true;
    probeServer().then((result) => {
      serverProbe = result;
      serverProbeAt = Date.now();
    }).finally(() => {
      serverProbing = false;
      updateSyncStatus();
    });
  }
  return serverStatus({ ...input, server: serverProbe });
}

const LEVEL_CLASS = {
  [ServerLevel.OK]: "saved",
  [ServerLevel.BUSY]: "syncing",
  [ServerLevel.PROBLEM]: "saving",
  [ServerLevel.OFFLINE]: "offline",
  [ServerLevel.NONE]: "offline",
};

/**
 * Draw the server indicator (header, right): the connection to the server
 * and whether this device's changes reach it. `state` is the open map's
 * connection when the caller has it; where the map is stored is shown next
 * to its title (interfaces/classic/ui/map-storage.js).
 */
export function updateSyncStatus(state) {
  const indicator = document.getElementById("save-indicator");
  const text = document.getElementById("save-text");
  const loginBtn = document.getElementById("login-to-sync-btn");
  if (!indicator || !text) return;

  // Hide login button by default (shown only when session expired + disconnected)
  if (loginBtn) loginBtn.classList.add("hidden");

  const status = computeServerStatus(state);
  lastServerStatus = status;
  indicator.className = LEVEL_CLASS[status.level] || "offline";
  indicator.dataset.level = status.level;
  emit(Events.SERVER_STATUS_CHANGED, status);

  if (!cb.getCurrentUser()) {
    // Phase 4: in Tauri local-only mode (no server configured) the data is
    // on real disk: say so, with the vault path in the tooltip.
    if (document.body.dataset.mode === "local-only") {
      text.textContent = t("app.status.savedToFile");
      const vaultPath = document.body.dataset.vaultPath;
      text.title = vaultPath ? t("app.status.vaultPath", { path: vaultPath }) : "";
    } else {
      text.textContent = t(status.text);
      text.title = t(status.hint);
    }
    // Show login button ONLY if: session expired + user has stored key + server is reachable
    // This means user is offline because they need to renew their session
    if (loginBtn &&
        cb.AuthHandlers.isSessionExpired && cb.AuthHandlers.isSessionExpired() &&
        cb.AuthHandlers.retrieveMasterKey && cb.AuthHandlers.retrieveMasterKey()) {
      checkServerHealth().then(serverOnline => {
        // Re-verify conditions still apply (avoid race with session restore)
        if (serverOnline &&
            !cb.getCurrentUser() &&
            cb.AuthHandlers.isSessionExpired && cb.AuthHandlers.isSessionExpired() &&
            cb.AuthHandlers.retrieveMasterKey && cb.AuthHandlers.retrieveMasterKey()) {
          loginBtn.classList.remove("hidden");
        }
      });
    }
    return;
  }

  text.textContent = t(status.text, status.params);
  text.title = hintText(status, t);
}

/**
 * Setup CRDT event listeners
 */
export function setupCRDTEventListeners() {
  // Clean up any existing listeners first (prevents duplicates on re-login)
  cleanupCRDTEventListeners();

  // Also the vault's own connection (it carries the server status while a
  // map on this device is open) and the vault index (a map moved online).
  for (const event of [Events.SYNC_PAUSED, Events.SYNC_PAUSE_CHANGED, Events.USER_CRDT_STATE_CHANGED, Events.VAULT_INDEX_UPDATED]) {
    crdtEventUnsubscribers.push(on(event, () => updateSyncStatus(cb.CRDTBridge.getConnectionState())));
  }

  // Listen for sync state changes
  crdtEventUnsubscribers.push(
    on(Events.SYNC_STATE_CHANGED, ({ state, shareId }) => {
      // Only update indicator for the main document sync, not shared docs
      if (!shareId) {
        updateSyncStatus(state);
      }
    }),
  );

  // Listen for remote updates
  crdtEventUnsubscribers.push(
    on(Events.SYNC_REMOTE_UPDATE, ({ source }) => {
      console.log("[CRDT] Remote update received from:", source);
      // Views will automatically re-render through Store subscription
    }),
  );

  // Phase 1.2 (PLAN17): Listen for docNotes changes (local or remote)
  // and refresh notes views to show updated content
  crdtEventUnsubscribers.push(
    on(Events.DOC_NOTES_CHANGED, ({ origin }) => {
      // Only refresh for remote changes - local changes are already visible
      if (origin === 'remote' || origin === 'sync') {
        // Refresh notes full view if it's active
        if (cb.NotesView.isOpen && cb.NotesView.isOpen()) {
          cb.NotesView.refresh();
        }
        // Refresh notes overlay if it's open
        if (cb.NotesOverlay.isOpen && cb.NotesOverlay.isOpen()) {
          cb.NotesOverlay.refresh();
        }
      }
    }),
  );

  // Remote execution-map changes (another device ticked / sent / pinned):
  // refresh the notes views the same way as remote doc-note changes.
  crdtEventUnsubscribers.push(
    on(Events.EXECUTION_CHANGED, ({ origin }) => {
      if (origin !== 'remote') return;
      if (cb.NotesView.isOpen && cb.NotesView.isOpen()) cb.NotesView.refresh();
      if (cb.NotesOverlay.isOpen && cb.NotesOverlay.isOpen()) cb.NotesOverlay.refresh();
    }),
  );

  // Listen for sync errors
  crdtEventUnsubscribers.push(
    on(Events.SYNC_ERROR, ({ error, message }) => {
      console.error("[CRDT] Sync error:", error);
      updateSyncStatus(ConnectionState.ERROR);
      // Show user-visible warning for actionable errors (e.g. DEK rotation failure)
      if (message && cb.showToast) {
        cb.showToast(message, { type: "warning" });
      }
    }),
  );

  // Listen for corrupted documents
  crdtEventUnsubscribers.push(
    on(Events.DOCUMENT_CORRUPTED, async ({ docId, docName, reason }) => {
      console.error("[CRDT] Document corrupted:", { docId, docName, reason });

      // Show error dialog with option to delete
      const confirmed = await cb.confirmModal({
        title: t("app.corrupted.title"),
        message: t("app.corrupted.message", { name: docName, reason }),
        confirmText: t("app.corrupted.delete"),
        cancelText: t("app.corrupted.goToVault"),
        confirmClass: "danger",
      });

      if (confirmed) {
        // Delete the corrupted document
        try {
          await cb.VaultStorage.deleteMap(docId);
          cb.showToast(t("app.corrupted.deleted"), "success");
        } catch (err) {
          console.error("Failed to delete corrupted document:", err);
          cb.showToast(t("app.corrupted.deleteFailed"), "error");
        }
      }

      // Navigate back to vault: switch to a safe view and open the vault panel.
      // (Store has no generic setState, and "vault" isn't a renderable view —
      // the vault is a panel overlay opened via VaultUI.open.)
      cb.Store.setView("dashboard");
      cb.VaultUI.open();
    }),
  );

  // Sync userSetProjectDates to CRDT metadata when it changes
  // This setting affects Gantt view and should be shared with collaborators
  crdtEventUnsubscribers.push(
    on(Events.DATA_CHANGED, ({ action, changes }) => {
      if (
        action === "setUserSetProjectDates" &&
        changes.includes("userSetProjectDates")
      ) {
        const value = cb.Store.get("userSetProjectDates");
        console.log(
          "[CRDT] Syncing userSetProjectDates to CRDT metadata:",
          value,
        );
        cb.CRDTBridge.setMetadata("userSetProjectDates", value);
      }
    }),
  );

  // Listen for share revocation
  crdtEventUnsubscribers.push(
    on(
      Events.SHARE_REVOKED,
      async ({
        shareId,
        recoveredDocId,
        title,
        originalTitle: eventOriginalTitle,
        wasActive,
        recoveryFailed,
      }) => {
        console.log("[CRDT] Share revoked:", {
          shareId,
          recoveredDocId,
          title,
          wasActive,
          recoveryFailed,
        });

        // If this share was handled via the deferred path (SHARE_REVOCATION_READY),
        // the user already saw the modal and the recovered doc was already opened.
        // Do NOT show a second modal or re-open the doc.
        if (_deferredRevocationShares.has(shareId)) {
          _deferredRevocationShares.delete(shareId);
          console.log("[CRDT] Share revocation already handled via deferred path, skipping");
          return;
        }

        if (wasActive && recoveredDocId) {
          // User was viewing standalone share that got revoked and recovery
          // already completed (via notification Recover button). Just
          // surface a toast and auto-open the recovered doc.
          console.log("[CRDT] Loading recovered map:", recoveredDocId);
          try {
            const docState = getDocState(recoveredDocId);
            if (docState) {
              delete docState.focusId;
              delete docState.currentNoteId;
              saveDocState(recoveredDocId, docState);
            }

            // The recovered title's suffix is translated: prefer the title
            // the event carries from before it was added.
            const originalTitle = eventOriginalTitle || title.replace(" (recovered)", "");
            showToast(
              t("app.revoked.opening", { title: originalTitle, recovered: title }),
              { type: "success", duration: 5000 },
            );

            await cb.handleDocOpen(recoveredDocId);
          } catch (error) {
            console.error("[CRDT] Failed to load recovered map:", error);
          }
        } else if (wasActive && recoveryFailed) {
          // Recovery-failed path.
          showToast(
            t("app.revoked.noRecovery", { title: title || t("app.revoked.theSharedMap") }),
            { type: "error", duration: 7000 },
          );
          cb.VaultUI.open();
        }
        // Notification is updated via Events.SHARE_REVOKED handler in notifications.js
      },
    ),
  );

  // Listen for deferred revocation: the user was actively viewing a share
  // that just got revoked via WS. Recovery is held until the user confirms
  // via the revocation-prompt modal (consistent with the linked-doc flow).
  crdtEventUnsubscribers.push(
    on(Events.SHARE_REVOCATION_READY, async ({ shareId, title, preStopTreeData }) => {
      console.log("[CRDT] Share revocation ready (deferred):", shareId);

      // Mark as deferred so the SHARE_REVOKED listener doesn't show a
      // second modal once recovery finishes.
      _deferredRevocationShares.add(shareId);

      const originalTitle = (title || "").replace(" (recovered)", "");
      const proceed = await showRevocationPrompt({
        docName: originalTitle || t("app.revoked.theSharedDocument"),
        context: t("app.revoked.wasEditing"),
        recoverText: t("app.revoked.recoverOpen"),
        laterText: t("common.later"),
      });

      if (!proceed) {
        // User declined to recover now. Leave the share_revoked notification
        // in the inbox and move them off the dead doc.
        _deferredRevocationShares.delete(shareId);
        showToast(
          t("app.revoked.deferred", { title: originalTitle }),
          { type: "warning", duration: 6000 },
        );
        cb.VaultUI.open();
        return;
      }

      // User chose to recover — run the full recovery flow.
      const result = await performDocumentRevocation(
        shareId,
        title,
        true, // wasActive
        { fromWebSocket: true },
        preStopTreeData,
      );

      if (result.success && result.recoveredDocId) {
        console.log("[CRDT] Loading recovered map:", result.recoveredDocId);
        try {
          const docState = getDocState(result.recoveredDocId);
          if (docState) {
            delete docState.focusId;
            delete docState.currentNoteId;
            saveDocState(result.recoveredDocId, docState);
          }
          showToast(
            // The recovered map's name, as share-revocation.js creates it.
            t("app.revoked.recoveredAs", { title: t("share.recovery.copyName", { title: originalTitle }) }),
            { type: "success", duration: 4000 },
          );
          await cb.handleDocOpen(result.recoveredDocId);
        } catch (error) {
          console.error("[CRDT] Failed to load recovered map:", error);
        }
      } else {
        _deferredRevocationShares.delete(shareId);
        showToast(
          t("app.revoked.recoverFailed", { title: originalTitle }),
          { type: "error", duration: 6000 },
        );
        cb.VaultUI.open();
      }
    }),
  );
}

/**
 * Clean up CRDT event listeners (called on logout to prevent duplicates)
 */
export function cleanupCRDTEventListeners() {
  crdtEventUnsubscribers.forEach((unsubscribe) => unsubscribe());
  crdtEventUnsubscribers = [];
  _deferredRevocationShares.clear();
}

// States a socket can be woken out of. CONNECTING and the SYNCING/SYNCED pair
// are excluded because an attempt is already in flight.
const WAKEABLE_STATES = new Set([
  ConnectionState.DISCONNECTED,
  ConnectionState.RECONNECTING,
  ConnectionState.ERROR,
]);

/**
 * Reset the reconnect backoff on every sync socket that is currently down.
 *
 * Covers the main doc, the user socket, and one socket per open share — the
 * shares were previously missed even by the 'online' handler, so a share left
 * open across a network drop could out-wait its owner document.
 *
 * Deliberately not time-debounced. An earlier version rate-limited the sweep,
 * which turned out to suppress real signals: a sweep triggered by one socket
 * connecting would swallow the sweep from the next socket a few hundred ms
 * later, and that second one is exactly the signal a still-sleeping peer needs.
 * Gating on "is anything actually down" instead makes the redundant sweeps in a
 * reconnect burst cost one Set lookup each and log nothing, with no window in
 * which a genuine signal can be lost.
 *
 * Safe to call at any time: both triggerReconnect implementations return early
 * when the socket is connected, connecting or explicitly stopped, so this never
 * disturbs a healthy connection and never resurrects one that was deliberately
 * torn down (e.g. a revoked share that gave up retrying).
 */
function wakeSockets(reason) {
  const targets = [];

  if (WAKEABLE_STATES.has(cb?.CRDTBridge?.getConnectionState?.())) {
    targets.push(['doc', () => cb.CRDTBridge.triggerReconnect()]);
  }
  if (WAKEABLE_STATES.has(cb?.UserCRDTBridge?.getConnectionState?.())) {
    targets.push(['user', () => cb.UserCRDTBridge.triggerReconnect()]);
  }
  for (const shareId of getActiveSharedDocs()) {
    const manager = getSharedSyncManager(shareId);
    if (manager && WAKEABLE_STATES.has(manager.getState())) {
      targets.push([`share ${shareId}`, () => manager.triggerReconnect()]);
    }
  }

  if (targets.length === 0) return;

  console.log(`[Network] Waking ${targets.length} sync socket(s) after ${reason}: ${targets.map(([n]) => n).join(', ')}`);
  for (const [name, wake] of targets) {
    // One bad manager must not stop the rest from being woken.
    try {
      wake();
    } catch (err) {
      console.warn(`[Network] Wake failed for ${name}:`, err);
    }
  }
}

/**
 * Setup browser network status listeners.
 * Detects offline/online state changes at the OS level for immediate feedback.
 */
export function setupNetworkListeners() {
  window.addEventListener('offline', () => {
    console.log('[Network] Browser reports offline');
    updateSyncStatus(ConnectionState.DISCONNECTED);
  });

  window.addEventListener('online', () => {
    console.log('[Network] Browser reports online - triggering reconnect');
    wakeSockets('browser-online');
  });

  // Cross-socket recovery. Each socket (main doc, user, one per open share)
  // runs its own independent backoff ladder capped at 30 s, and the only
  // things that reset a ladder early are the browser 'online' event and a
  // tab resume. Neither fires when the *server* goes away and comes back —
  // the client's own network never drops — so after a backend restart the
  // ladders stay desynchronised: whichever socket happens to retry at the
  // right moment reconnects, and the others keep sleeping out a backoff
  // that is already stale. Observed on a prod reboot: the user socket
  // reconnected and fully resynced while the main doc sat on a 17 s timer
  // with the server already serving.
  //
  // Any socket reaching CONNECTED is proof the backend is reachable, so use
  // it to wake the others. Both triggerReconnect implementations no-op when
  // the socket is already connected or connecting, which is what stops this
  // from looping back on itself.
  const onPeerConnected = (state, source) => {
    if (state !== ConnectionState.CONNECTED) return;
    wakeSockets(source);
  };
  on(Events.SYNC_STATE_CHANGED, ({ state }) => onPeerConnected(state, 'doc-socket'));
  on(Events.USER_CRDT_STATE_CHANGED, ({ state }) => onPeerConnected(state, 'user-socket'));
  // Files attached offline: the server indicator says while they wait.
  on(Events.WAITING_FILES_CHANGED, () => updateSyncStatus());
  on(Events.SESSION_ENDED, () => {
    if (cb.AuthHandlers?.isSigningOutHere?.()) return;
    sessionEnded = true;
    updateSyncStatus();
  });
  on(Events.AUTH_LOGIN, () => { sessionEnded = false; });
  // The server refused a save as busy, or stores again.
  on(Events.SERVER_BUSY_CHANGED, () => updateSyncStatus());
  // Signed in again in place (interfaces/classic/ui/session-resume.js): the sockets, still
  // retrying, connect now and send what waited.
  on(Events.SESSION_RESUMED, () => {
    sessionEnded = false;
    updateSyncStatus();
    wakeSockets("session-resumed");
  });
  on(Events.STORAGE_POLICY_CHANGED, () => updateSyncStatus());

  // Tab-resume detection. Mobile browsers and PWAs (notably iOS Safari)
  // suspend background tabs aggressively: setInterval/setTimeout get throttled
  // or paused, and the underlying TCP socket can be killed without notifying
  // JS — readyState stays OPEN while the connection is dead. The browser
  // 'online' event does NOT fire on tab resume because the OS still has
  // network. Without this, the doc-WS sits zombie-SYNCED until the next
  // heartbeat fires (up to ~40s) or the user reopens the doc — which is what
  // produced the "edits took hours to appear on mobile" report.
  //
  // Fire a probe on both visibilitychange→visible and pageshow (the latter
  // also covers bfcache restore on iOS). The probe is non-destructive: it
  // sends an immediate heartbeat if state thinks SYNCED, or jumps the
  // reconnect backoff if state is DISCONNECTED/RECONNECTING. Debounced so
  // duplicate events (some browsers fire both) don't pile probes up.
  let lastProbeAt = 0;
  const PROBE_DEBOUNCE_MS = 1000;
  const probeIfVisible = (source) => {
    if (typeof document === 'undefined' || document.hidden) return;
    const now = Date.now();
    if (now - lastProbeAt < PROBE_DEBOUNCE_MS) return;
    lastProbeAt = now;
    console.log(`[Network] Tab resumed (${source}) — probing sync connections`);
    cb.CRDTBridge.probeConnection();
    cb.UserCRDTBridge.probeConnection();
  };
  document.addEventListener('visibilitychange', () => probeIfVisible('visibilitychange'));
  window.addEventListener('pageshow', () => probeIfVisible('pageshow'));
  // Refused for too many connections: coming back to this window (maybe
  // after closing another) tries again at once.
  window.addEventListener('focus', () => {
    if (cb.CRDTBridge.isRefusedTooMany?.() || cb.UserCRDTBridge.isRefusedTooMany?.()) probeIfVisible('focus');
  });

  // Log initial state
  console.log('[Network] Initial online state:', navigator.onLine);
}
